import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of, tap, finalize, shareReplay, forkJoin } from 'rxjs';
import { API_V1_URL } from '../../../core/config/api.config';
import { AuthService } from '../../../core/services/auth.service';
import {
  Benutzer,
  BenutzerPageResponse,
  BenutzerResponse,
  CreateBenutzerRequest,
  UpdateBenutzerRequest,
  CreateSuperAdminRequest,
  UpdateSuperAdminRequest,
} from '../models/benutzer.model';
import { Filiale } from '../models/filiale.model';
import { Rolle, RolleRequest } from '../models/rolle.model';

export interface EntityLock {
  entityType: string;
  entityId: string;
  lockedBy: string;
  lockedByName: string;
  lockedAt: string;
  expiresAt: string;
}

export interface LockProblemResponse {
  title?: string;
  status?: number;
  detail?: string;
  message?: string;
  code: string;
  lock?: EntityLock;
  blockedKeys?: {
    key: string;
    lockId: string;
    lockedByName: string;
    lockedAt: string;
    expiresAt: string;
  }[];
}

interface TenantResponse {
  tId?: string;
  TId?: string;

  email?: string;
  Email?: string;

  benutzer?: string;
  Benutzer?: string;

  vorname?: string;
  Vorname?: string;

  name?: string;
  Name?: string;

  isActive?: boolean;
  IsActive?: boolean;
}
export interface BulkAssignResult {
  usersUpdated: number;
  rollesAssigned: number;
  filialenAssigned: number;
}
export interface BulkAssignRolesResponse {
  usersUpdated: number;
  rollesAffected: number;
  skippedAdmins: BulkAssignSkippedUser[];
  skippedLastRole: BulkAssignSkippedUser[];
  skipReason?: string;
}

export interface BulkAssignSkippedUser {
  userId: string;
  name: string;
}
@Injectable({
  providedIn: 'root',
})
export class BenutzerverwaltungService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private rollenCache: Rolle[] | null = null;
  private rollenRequest$?: Observable<Rolle[]>;
  private readonly apiBaseUrl = API_V1_URL;

  getBenutzer(): Observable<Benutzer[]> {
    if (this.authService.isCopagoAdmin()) {
      return this.http
        .get<TenantResponse[]>(`${this.apiBaseUrl}/tenants`)
        .pipe(
          map((tenants) =>
            tenants.map((tenant) => this.mapTenantToBenutzer(tenant))
          )
        );
    }

    return this.http
      .get<BenutzerPageResponse | BenutzerResponse[]>(
        `${this.apiBaseUrl}/users`
      )
      .pipe(
        map((response) => {
          const items = Array.isArray(response)
            ? response
            : response.items ?? [];

          return items.map((user) => this.mapBenutzer(user));
        })
      );
  }

  createBenutzer(request: CreateBenutzerRequest): Observable<Benutzer> {
    return this.http
      .post<BenutzerResponse>(`${this.apiBaseUrl}/users`, request)
      .pipe(map((user) => this.mapBenutzer(user)));
  }

  updateBenutzer(
    id: string,
    request: UpdateBenutzerRequest
  ): Observable<Benutzer> {
    return this.http
      .put<BenutzerResponse>(`${this.apiBaseUrl}/users/${id}`, request)
      .pipe(map((user) => this.mapBenutzer(user)));
  }

  deleteBenutzer(id: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(
      `${this.apiBaseUrl}/users/${id}`
    );
  }

  setBenutzerRollen(userId: string, rollenIds: string[]): Observable<Benutzer> {
    return this.http
      .put<BenutzerResponse>(`${this.apiBaseUrl}/users/${userId}/rollen`, {
        rollenIds,
      })
      .pipe(map((user) => this.mapBenutzer(user)));
  }

  bulkChangeBenutzerRollen(
    userIds: string[],
    rolleIds: string[],
    action: 1 | 2 | 0
  ): Observable<BulkAssignRolesResponse> {
    return this.http.post<BulkAssignRolesResponse>(
      `${this.apiBaseUrl}/users/bulk-assign/roles`,
      {
        userIds,
        rolleIds,
        action,
      }
    );
  }

  setBenutzerFilialen(
    userId: string,
    filialeIds: number[]
  ): Observable<Benutzer> {
    return this.http
      .put<BenutzerResponse>(`${this.apiBaseUrl}/users/${userId}/filialen`, {
        filialeIds,
      })
      .pipe(map((user) => this.mapBenutzer(user)));
  }

  getRollen(forceRefresh = false): Observable<Rolle[]> {
    if (!forceRefresh && this.rollenCache) {
      return of(this.rollenCache);
    }

    if (!forceRefresh && this.rollenRequest$) {
      return this.rollenRequest$;
    }

    this.rollenRequest$ = this.http.get<Rolle[]>(`${this.apiBaseUrl}/rollen`).pipe(
      tap((rollen) => {
        this.rollenCache = rollen;
      }),
      finalize(() => {
        this.rollenRequest$ = undefined;
      }),
      shareReplay(1)
    );

    return this.rollenRequest$;
  }

  createRolle(request: RolleRequest): Observable<Rolle> {
    return this.http.post<Rolle>(`${this.apiBaseUrl}/rollen`, request).pipe(
      tap(() => this.clearRollenCache())
    );
  }

  updateRolle(id: string, request: RolleRequest): Observable<Rolle> {
    return this.http.put<Rolle>(`${this.apiBaseUrl}/rollen/${id}`, request).pipe(
      tap(() => this.clearRollenCache())
    );
  }

  deleteRolle(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiBaseUrl}/rollen/${id}`).pipe(
      tap(() => this.clearRollenCache())
    );
  }

  private clearRollenCache(): void {
    this.rollenCache = null;
    this.rollenRequest$ = undefined;
  }

  getModule(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiBaseUrl}/modules`);
  }

  getFilialen(): Observable<Filiale[]> {
    return this.http.get<Filiale[]>(`${this.apiBaseUrl}/filialen`);
  }

  private readonly activeLocks = new Map<string, string>();

  private getLockMapKey(entityType: 'user' | 'rolle', entityId: string): string {
    return `${entityType}:${entityId}`;
  }

  private acquireLocks(keys: string[]): Observable<{ message: string }> {
    return this.http
      .post<{ lockId: string; message?: string }>(`${this.apiBaseUrl}/locks/acquire`, {
        keys,
      })
      .pipe(
        tap((response) => {
          if (response.lockId) {
            keys.forEach((key) => this.activeLocks.set(key, response.lockId));
          }
        }),
        map((response) => ({
          message: response.message ?? 'Locks acquired.',
        }))
      );
  }

  private releaseLocks(keys: string[]): Observable<{ message: string }> {
    const lockIds = Array.from(
      new Set(
        keys
          .map((key) => this.activeLocks.get(key))
          .filter((lockId): lockId is string => !!lockId)
      )
    );

    if (!lockIds.length) {
      return of({ message: 'No active locks.' });
    }

    return forkJoin(
      lockIds.map((lockId) =>
        this.http.delete<{ message?: string }>(`${this.apiBaseUrl}/locks/${lockId}`)
      )
    ).pipe(
      tap(() => {
        keys.forEach((key) => this.activeLocks.delete(key));
      }),
      map(() => ({
        message: 'Locks released.',
      }))
    );
  }

  acquireUserLock(userId: string, rolleIds: string[] = []): Observable<{ message: string }> {
    const keys = [
      this.getLockMapKey('user', userId),
      ...rolleIds.map((rolleId) => this.getLockMapKey('rolle', rolleId)),
    ];

    return this.acquireLocks(keys);
  }

  releaseUserLock(userId: string, rolleIds: string[] = []): Observable<{ message: string }> {
    const keys = [
      this.getLockMapKey('user', userId),
      ...rolleIds.map((rolleId) => this.getLockMapKey('rolle', rolleId)),
    ];

    return this.releaseLocks(keys);
  }

  acquireUserLocks(userIds: string[], rolleIds: string[] = []): Observable<{ message: string }> {
    const keys = [
      ...userIds.map((userId) => this.getLockMapKey('user', userId)),
      ...rolleIds.map((rolleId) => this.getLockMapKey('rolle', rolleId)),
    ];

    return this.acquireLocks(keys);
  }

  releaseUserLocks(userIds: string[], rolleIds: string[] = []): Observable<{ message: string }> {
    const keys = [
      ...userIds.map((userId) => this.getLockMapKey('user', userId)),
      ...rolleIds.map((rolleId) => this.getLockMapKey('rolle', rolleId)),
    ];

    return this.releaseLocks(keys);
  }

  acquireRolleLock(rolleId: string): Observable<{ message: string }> {
    return this.acquireLocks([this.getLockMapKey('rolle', rolleId)]);
  }

  releaseRolleLock(rolleId: string): Observable<{ message: string }> {
    return this.releaseLocks([this.getLockMapKey('rolle', rolleId)]);
  }

  private mapBenutzer(user: BenutzerResponse): Benutzer {
    const fullName =
      user.fullName?.trim() || `${user.vorname} ${user.name}`.trim();

    return {
      id: user.id,
      benutzername: user.benutzername || user.email,
      email: user.email,
      vorname: user.vorname,
      nachname: user.name,
      name: fullName,
      rollen: user.rollen ?? [],
      allowedFilialen: user.allowedFilialen ?? [],
      filialen: (user.allowedFilialen ?? []).map((id) => String(id)),
      module: this.getModulesFromUserRoles(user.rollen ?? [], user.isAdmin),
      isAdmin: user.isAdmin,
      isLockedOut: user.isLockedOut,
      isDeactivated: user.isDeactivated,
      mustChangePassword: user.mustChangePassword,
      lastLoginAt: user.lastLoginAt,
      aktiv: !user.isDeactivated && !user.isLockedOut,
    };
  }

  private mapTenantToBenutzer(tenant: TenantResponse): Benutzer {
    const tenantId = tenant.tId ?? tenant.TId ?? '';
    const email = tenant.email ?? tenant.Email ?? '';
    const benutzername = tenant.benutzer ?? tenant.Benutzer ?? email;
    const vorname = tenant.vorname ?? tenant.Vorname ?? '';
    const nachname = tenant.name ?? tenant.Name ?? '';
    const isActive = tenant.isActive ?? tenant.IsActive ?? false;

    const fullName = `${vorname} ${nachname}`.trim();

    return {
      id: tenantId,
      benutzername,
      email,
      vorname,
      nachname,
      name: fullName || benutzername || email,
      rollen: [
        {
          id: 'super-admin',
          name: 'Super Admin',
          istAdminRolle: true,
          module: ['Alle'],
        },
      ],
      allowedFilialen: [],
      filialen: [],
      module: ['Alle'],
      isAdmin: true,
      isLockedOut: false,
      isDeactivated: !isActive,
      mustChangePassword: false,
      lastLoginAt: null,
      aktiv: isActive,
    };
  }

  private getModulesFromUserRoles(
    rollen: { istAdminRolle: boolean; module?: string[] }[],
    isAdmin: boolean
  ): string[] {
    if (isAdmin || rollen.some((rolle) => rolle.istAdminRolle)) {
      return ['Alle'];
    }

    const modules = rollen.flatMap((rolle) => rolle.module ?? []);

    return Array.from(new Set(modules));
  }

  createSuperAdmin(request: CreateSuperAdminRequest): Observable<unknown> {
    return this.http.post<unknown>(
      `${this.apiBaseUrl}/superadmin/seed`,
      request
    );
  }

  updateSuperAdmin(
    tenantId: string,
    request: UpdateSuperAdminRequest
  ): Observable<unknown> {
    return this.http.put<unknown>(
      `${this.apiBaseUrl}/superadmin/${tenantId}`,
      request
    );
  }

  updateTenantStatus(tId: string, isActive: boolean): Observable<void> {
    return this.http.put<void>(
      `${this.apiBaseUrl}/tenants/${tId}/status`,
      {
        isActive,
      }
    );
  }
}